import React from 'react';
import ikeaLogoImg from '@/assets/icons/ikea-logo.jpg';
import { CardLogo } from './card-logo';
import { isBrowser } from '@/helpers/is-browser';
import { useUser } from '@/providers/UserProvider';
import { useIsMPLivliFamily } from '@/providers/ConfigProvider';
import type {
  ListCard,
  ListType,
} from '@/helpers/list-page/interfaces/list-card.interface';
import type { IkeaFamilyBenefitItem } from '@/lib/getIkeaFamilyBenefits';

interface CardIkeaFamilyBenefitProps {
  card: IkeaFamilyBenefitItem;
  listType?: ListType;
  fixedWidth?: boolean;
  fixedHeight?: boolean;
  pageName?: string;
}

// Static IKEA logo rendered through the shared CardLogo component.
const IKEA_LOGO_CARD = {
  logo: {
    url: ikeaLogoImg.src,
    description: 'IKEA Family',
    width: String(ikeaLogoImg.width),
    height: String(ikeaLogoImg.height),
  },
} as unknown as ListCard;

// Only allow https IKEA URLs; the value comes from an external API, so guard
// against javascript:/data: schemes and open redirects to non-IKEA hosts.
const getSafeIkeaUrl = (value?: string): string | null => {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol === 'https:' && /(^|\.)ikea\.com$/.test(url.hostname)) {
      return url.toString();
    }
  } catch {
    // invalid URL
  }
  return null;
};

const sendIkeaFamilyBenefitClickEvent = (
  pageName?: string,
  userStatus?: string,
) => {
  if (isBrowser && dataLayer) {
    dataLayer.push({
      event: 'ikea_family_benefit_click',
      page_title: pageName,
      user_status: userStatus,
    });
  }
};

export const CardIkeaFamilyBenefit = ({
  card,
  listType,
  fixedWidth = false,
  fixedHeight = false,
  pageName,
}: CardIkeaFamilyBenefitProps) => {
  const { user } = useUser();
  const isLivliFamily = useIsMPLivliFamily();
  const userStatus = isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined;

  const imageUrl = getSafeIkeaUrl(
    card?.media?.find((m) => m?.type?.toUpperCase() === 'IMAGE')?.url,
  );
  const href = getSafeIkeaUrl(card?.callToAction?.webUrl);
  const description = card?.description?.small;

  if (!href) return null;

  // Match the neighbouring cards' logo dimensions per listing (responsive):
  // offer cards use 48px -> 80px, event cards use 80px on mobile and 64px from
  // md up. The homepage card uses a fixed 80px logo.
  const logoSizeClass = fixedHeight
    ? 'h-20 w-20'
    : listType === 'offer'
      ? 'h-12 w-12 md:h-20 md:w-20'
      : 'h-20 w-20 md:h-16 md:w-16';

  // Logo offset from the top-left corner. The homepage card uses a fixed 14px
  // inset; listings match their neighbouring cards' insets.
  const logoPositionClass = fixedHeight
    ? 'top-6 left-6'
    : listType === 'offer'
      ? 'top-3 left-3 md:top-4 md:left-4'
      : 'top-5 left-5';

  // Mobile card shape: offer listing keeps its taller ~2:3 crop; the event
  // listing uses a 3:4 crop to match the homepage mobile card. From md up the
  // card fills its grid cell so it stays aligned with the neighbouring cards.
  const shapeClass = fixedHeight
    ? ''
    : listType === 'offer'
      ? 'aspect-0.65/1 md:aspect-auto md:h-full md:min-h-[320px]'
      : 'aspect-0.75/1 md:aspect-auto md:h-full md:min-h-[320px]';

  // Text sizes only shrink on the offer listing's mobile view. Everywhere else
  // (offer listing from md up, event listing, and the homepage card) keeps the
  // 24px title / 14px description.
  const isOfferListing = listType === 'offer' && !fixedHeight;
  const titleSizeClass = isOfferListing
    ? 'text-headings md:text-headingm'
    : 'text-headingm';
  const descriptionSizeClass = isOfferListing
    ? 'text-bodys md:text-bodym'
    : 'text-bodym';

  return (
    <div className="relative h-full mb-5">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={card?.name}
        onClick={() => sendIkeaFamilyBenefitClickEvent(pageName, userStatus)}
        style={fixedHeight ? { aspectRatio: '3 / 4' } : undefined}
        className={`ikea group relative flex ${shapeClass} cursor-pointer flex-col justify-end overflow-hidden rounded-xl focus:outline focus:outline-3 focus:outline-offset-3 focus:outline-text-light ${fixedWidth ? 'w-[316px]' : 'w-full'}`}
      >
        {imageUrl && (
          <img
            className="absolute inset-0 h-full w-full object-cover transition-all"
            src={imageUrl + '?fm=webp&q=50'}
            alt={card?.name || 'IKEA Family benefit'}
          />
        )}

        {/* IKEA logo */}
        <CardLogo
          card={IKEA_LOGO_CARD}
          className={`!absolute z-0 flex items-center justify-center overflow-hidden rounded-[12px] bg-brand-white sm:rounded-lg [&_img]:h-full [&_img]:w-full ${logoPositionClass} ${logoSizeClass}`}
        />

        <div className="relative z-[1] flex h-full flex-col justify-end bg-gradient-to-t from-transparency-overlay-solid px-5 pb-6 pt-16 transition-all duration-200 ease-in-out lg:group-hover:bg-transparency-backdrop">
          <h2
            className={`font-title ${titleSizeClass} text-brand-white transition-all duration-200 ease-in-out lg:group-hover:text-brand-yellow-1`}
          >
            {card?.name}
          </h2>
          {description && (
            <p
              className={`line-clamp-3 ${descriptionSizeClass} text-brand-white`}
            >
              {description}
            </p>
          )}
        </div>
      </a>
    </div>
  );
};

